import React, { useState } from "react";
import { GridActionsCellItem } from "@mui/x-data-grid";
import {
  CloudUploadOutlined,
  DownloadOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Divider,
  IconButton,
  Grid,
  Paper,
  Stack,
  Chip,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Constants from "../../services/Constants";
import {
  getCognitoAiUploadDetail,
  getS3ByImageId,
} from "../../services/CognitoAi";
import { formatFileSize, formatDate_12h } from "../../services/Utils";
import CognitoAiFileUpload from "./CognitoAiFileUpload";
import { useToast } from "../toast";
import { IMAGE_UPLOAD_ERRORS } from "../../services/Constants";

const DetailRow = ({ label, value }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "140px minmax(0, 1fr)" },
      gap: 1,
      py: 1,
      alignItems: "start",
      borderBottom: "1px solid",
      borderColor: "divider",
      "&:last-of-type": {
        borderBottom: "none",
        pb: 0,
      },
    }}
  >
    <Typography variant="body2" color="text.secondary" fontWeight={600}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
      {value || "-"}
    </Typography>
  </Box>
);

const CognitoAiDataTableRowActions = ({ rowData }) => {
  const [openDetails, setOpenDetails] = useState(false);
  const [uploadDetails, setUploadDetails] = useState(null);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [resumeUpload, setResumeUpload] = useState(null);
  const [resumeUploadStep, setResumeUploadStep] = useState(0);
  const [isLoadingResume, setIsLoadingResume] = useState(false);
  const { addToast } = useToast();

  const handleDownload = async () => {
    try {
      const details = await getS3ByImageId(rowData.uploadId);
      if (details?.downloadUrl) {
        window.open(details.downloadUrl, "_blank");
      } else {
        alert("Download URL not available. Upload may not be complete.");
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("Failed to download file");
    }
  };

  const handleViewDetails = async () => {
    try {
      const details = await getCognitoAiUploadDetail(rowData.uploadId);
      setUploadDetails(details);
      setOpenDetails(true);
    } catch (error) {
      console.error("Error fetching details:", error);
      alert("Failed to fetch details");
    }
  };

  const handleResumeUpload = async () => {
    if (window.navigator.onLine) {
      try {
        setIsLoadingResume(true);
        const details = await getCognitoAiUploadDetail(rowData.uploadId);
        setResumeUpload(details);
        setResumeUploadStep(0);
        setOpenUploadModal(true);
      } catch (error) {
        console.error("Error fetching upload details:", error);
        addToast({
          type: "error",
          message: "Failed to fetch upload details. Please try again.",
        });
      } finally {
        setIsLoadingResume(false);
      }
    } else {
      addToast({
        type: "error",
        message: IMAGE_UPLOAD_ERRORS.ERR_NETWORK_ISSUE,
      });
    }
  };

  const handleUploadModalClose = () => {
    setOpenUploadModal(false);
    setResumeUpload(null);
    setResumeUploadStep(0);
  };

  const handleUploadSuccess = () => {
    window.location.reload();
  };

  return (
    <>
      <Tooltip title="View Details">
        <GridActionsCellItem
          icon={<VisibilityOutlined />}
          label="Details"
          onClick={handleViewDetails}
          color="primary"
        />
      </Tooltip>
      {rowData.uploadStatus === Constants.UPLOAD_STATUS.Completed && (
        <Tooltip title="Download">
          <GridActionsCellItem
            icon={<DownloadOutlined />}
            label="Download"
            onClick={handleDownload}
            color="primary"
          />
        </Tooltip>
      )}
      {!(
        rowData.uploadStatus === Constants.UPLOAD_STATUS.Failed ||
        rowData.uploadStatus === Constants.UPLOAD_STATUS.Completed
      ) && (
        <Tooltip title="Reupload">
          <GridActionsCellItem
            icon={
              isLoadingResume ? (
                <CircularProgress fontSize="small" className="spin" />
              ) : (
                <CloudUploadOutlined />
              )
            }
            label="Reupload"
            onClick={handleResumeUpload}
            color="primary"
            disabled={isLoadingResume}
          />
        </Tooltip>
      )}
      <Dialog
        open={openDetails}
        onClose={() => setOpenDetails(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack spacing={1} pr={5}>
            <Typography variant="h6" fontWeight={700}>
              Cognito AI Upload Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review file metadata, upload status, and audit information.
            </Typography>
            {uploadDetails?.uploadStatus && (
              <Box>
                <Chip
                  label={uploadDetails?.uploadStatus?.toUpperCase()}
                  size="small"
                  color={
                    uploadDetails.uploadStatus ===
                    Constants.UPLOAD_STATUS.Completed
                      ? "success"
                      : uploadDetails.uploadStatus ===
                          Constants.UPLOAD_STATUS.Failed
                        ? "error"
                        : "warning"
                  }
                />
              </Box>
            )}
          </Stack>
          <IconButton
            aria-label="close"
            onClick={() => setOpenDetails(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {uploadDetails && (
            <Stack spacing={2}>
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2.5, borderRadius: 2.5, height: "100%" }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{ mb: 1.5 }}
                    >
                      File Information
                    </Typography>
                    <DetailRow
                      label="File Name"
                      value={uploadDetails.fileName}
                    />
                    <DetailRow label="Version" value={uploadDetails.version} />
                    <DetailRow
                      label="Product Variant"
                      value={uploadDetails.productVariant}
                    />
                    <DetailRow
                      label="File Size"
                      value={formatFileSize(parseInt(uploadDetails.fileSize))}
                    />
                    <DetailRow
                      label="File Type"
                      value={uploadDetails.fileType}
                    />
                    <DetailRow
                      label="Status"
                      value={uploadDetails.uploadStatus}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2.5, borderRadius: 2.5, height: "100%" }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{ mb: 1.5 }}
                    >
                      Upload Information
                    </Typography>
                    <DetailRow
                      label="Uploaded By"
                      value={uploadDetails.uploadedBy || "-"}
                    />
                    <DetailRow
                      label="Uploaded At"
                      value={formatDate_12h(uploadDetails.createdAt)}
                    />
                    <DetailRow
                      label="Updated At"
                      value={formatDate_12h(uploadDetails.updatedAt)}
                    />
                    <DetailRow
                      label="Updated By"
                      value={uploadDetails.updatedByName || "-"}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  {" "}
                  {uploadDetails.description && (
                    <Paper
                      variant="outlined"
                      sx={{ p: 2.5, borderRadius: 2.5 }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{ mb: 1.5 }}
                      >
                        Description
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                      >
                        {uploadDetails.description}
                      </Typography>
                    </Paper>
                  )}
                </Grid>
              </Grid>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
      <CognitoAiFileUpload
        open={openUploadModal}
        handleClose={handleUploadModalClose}
        onUploadSuccess={handleUploadSuccess}
        resumeUpload={resumeUpload}
        resumeUploadStep={resumeUploadStep}
      />
    </>
  );
};

export default CognitoAiDataTableRowActions;
